import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../../axiosBaseQuery';
import companiesSuppliersApiParser, {
  type CompaniesSuppliersData,
} from './companiesSuppliersApiParser';

// ---------- Types ----------
interface MakerItem {
  makerId: number;
  makerName: string;
}

interface ManagerItem {
  managerId: number;
  managerName: string;
}

export interface CompaniesSuppliers {
  id: string | number;
  srno: number;
  shortName: string;
  supplier: string;
  makers: MakerItem[];
  managers: ManagerItem[];
  makersIds: number[];
  managerIds: number[];
  status: string;
  actions: string;
}

export interface CompaniesSuppliersListResponse {
  data: CompaniesSuppliers[];
}

interface CreateCompaniesSuppliersPayload {
  companyId: number;
  shortName: string;
  active: boolean;
  makerIds: number[];
  managerIds: number[];
}

export interface GetCompaniesSuppliersListParams {
  [key: string]: any;
}

// ---------- API Slice ----------
const companiesSuppliersApi = createApi({
  reducerPath: 'companiesSuppliersApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['CompaniesSuppliers', 'CompaniesSuppliersData'],
  endpoints: (builder) => ({
    getCompaniesSuppliersList: builder.query<CompaniesSuppliers[], any>({
      query: (_params) => ({
        url: 'lubricant/suppliers',
        method: 'GET',
      }),
      transformResponse: (res: CompaniesSuppliersListResponse | any): CompaniesSuppliers[] =>
        companiesSuppliersApiParser.CompaniesSuppliersListParser(res),
    }),

    getCompaniesSuppliersData: builder.query<CompaniesSuppliersData, void>({
      query: () => ({
        url: 'lubricant/companies/short?partner=supplier',
        method: 'GET',
      }),
      transformResponse: (res: any): CompaniesSuppliersData => {
        const content = Array.isArray(res?.data?.content) ? res.data.content : [];
        const companies = content.map((c: any) => ({ value: String(c.id), label: String(c.name) }));
        return {
          companiesSuppliers: [],
          companies,
          makers: [],
          managers: [],
          statusOptions: [
            { value: 'Active', label: 'Active' },
            { value: 'Inactive', label: 'Inactive' },
          ],
        } as CompaniesSuppliersData;
      },
      providesTags: ['CompaniesSuppliersData'],
    }),

    createCompaniesSuppliers: builder.mutation<any, CreateCompaniesSuppliersPayload>({
      query: (payload) => ({
        url: 'lubricant/suppliers',
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: ['CompaniesSuppliers', 'CompaniesSuppliersData'],
    }),

    getManagersShortList: builder.query<{ value: number; label: string }[], void>({
      query: () => ({
        url: 'manager',
        method: 'GET',
      }),
      transformResponse: (res: any): { value: number; label: string }[] => {
        const content = Array.isArray(res?.data?.content) ? res.data.content : [];
        return content.map((m: any) => ({
          value: Number(m.mangerId ?? m.managerId ?? m.id),
          label: String(m.shortName || m.name || m.code),
        }));
      },
    }),

    updateCompaniesSuppliers: builder.mutation<
      any,
      {
        id: number;
        payload: {
          companyId: number;
          shortName: string;
          active: boolean;
          makerIds: number[];
          managerIds: number[];
        };
      }
    >({
      query: ({ id, payload }) => ({
        url: `lubricant/suppliers/${encodeURIComponent(id)}`,
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'CompaniesSuppliers' as const, id: id.toString() },
        'CompaniesSuppliers',
        'CompaniesSuppliersData',
      ],
    }),

    deleteCompaniesSuppliers: builder.mutation<any, number>({
      query: (id) => ({
        url: `lubricant/suppliers/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CompaniesSuppliers', 'CompaniesSuppliersData'],
    }),
  }),
});

// ---------- Exports ----------
export const companiesSuppliersReducer = companiesSuppliersApi.reducer;
export const companiesSuppliersMiddleware = companiesSuppliersApi.middleware;
export const companiesSuppliersReducerPath = companiesSuppliersApi.reducerPath;

export const {
  useGetCompaniesSuppliersListQuery,
  useGetCompaniesSuppliersDataQuery,
  useCreateCompaniesSuppliersMutation,
  useUpdateCompaniesSuppliersMutation,
  useDeleteCompaniesSuppliersMutation,
  useGetManagersShortListQuery,
} = companiesSuppliersApi;
